import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';

export interface ApiRequestOptions {
  headers?: HttpHeaders | Record<string, string | string[]>;
  context?: HttpContext;
  params?: HttpParams | Record<string, string | number | boolean | readonly (string | number | boolean)[]>;
  observe?: 'body';
  reportProgress?: boolean;
  responseType?: 'json';
  withCredentials?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export abstract class BaseApiService {
  protected readonly baseUrl: string = environment.apiUrl;

  constructor(protected http: HttpClient) {}

  /**
   * Performs a GET request
   */
  protected get<T>(endpoint: string, options?: ApiRequestOptions): Observable<T> {
    return this.http.get<T>(`${this.baseUrl}${endpoint}`, options);
  }

  /**
   * Performs a POST request
   */
  protected post<T>(endpoint: string, data: any, options?: ApiRequestOptions): Observable<T> {
    return this.http.post<T>(`${this.baseUrl}${endpoint}`, data, options);
  }

  /**
   * Performs a PUT request
   */
  protected put<T>(endpoint: string, data: any, options?: ApiRequestOptions): Observable<T> {
    return this.http.put<T>(`${this.baseUrl}${endpoint}`, data, options);
  }

  /**
   * Performs a PATCH request
   */
  protected patch<T>(endpoint: string, data: any, options?: ApiRequestOptions): Observable<T> {
    return this.http.patch<T>(`${this.baseUrl}${endpoint}`, data, options);
  }

  /**
   * Performs a DELETE request
   */
  protected delete<T>(endpoint: string, options?: ApiRequestOptions): Observable<T> {
    return this.http.delete<T>(`${this.baseUrl}${endpoint}`, options);
  }

  /**
   * Helper method to build HttpParams from an object
   */
  protected buildParams(params: Record<string, any>): HttpParams {
    let httpParams = new HttpParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        httpParams = httpParams.append(key, value.toString());
      }
    });
    
    return httpParams;
  }

  /**
   * Helper method to create HttpContext with loader control
   */
  protected createContext(showLoader = true): HttpContext {
    const context = new HttpContext();
    // Note: SHOW_LOADER token can be imported and used when needed
    // context.set(SHOW_LOADER, showLoader);
    return context;
  }
}
